import { type Tok, tokenize } from './parser.ts';

/**
 * Lisp pretty-printer for the chart/transform DSL.
 *
 * Re-lays-out a (possibly one-lined) expression as indented multi-line
 * source so it is visually balanceable — the single biggest predictor of
 * whether the agent can author and self-correct deep `(-> …)` pipelines
 * (a one-lined `case`/`#()` nest is where stray-paren loops happen).
 *
 * Style: a form is kept inline when it fits within `WIDTH`; otherwise its
 * head and first argument stay on the opening line and the remaining
 * arguments align under the first argument (classic Lisp indentation).
 *
 * Layout-only: it slices each token's surface text straight from the
 * source via the lexer, so string contents, `#(…)`, and keyword heads are
 * preserved byte-for-byte and the result always re-parses to the same AST.
 */
export function formatExpr(src: string): string {
  // Degrade to identity on anything the lexer rejects: some `<Expr>`
  // examples carry illustrative fragments (`…`, `...`) that are not
  // runnable DSL. Formatting is display sugar — it must never throw
  // through a render.
  try {
    const { tokens } = tokenize(src);
    const forms = buildForms(tokens, src);
    return forms.map((form) => render(form, 0)).join('\n');
  } catch {
    return src;
  }
}

// Target line width; forms wider than this break onto multiple lines.
const WIDTH = 72;

// Opener surface text → its closer. Keys double as the opener set:
// a token starts a group iff its surface text is one of these.
const CLOSE_FOR: Record<string, string> = { '(': ')', '#(': ')', '[': ']', '{': '}' };

type Node =
  | { readonly kind: 'atom'; readonly text: string }
  | { readonly kind: 'group'; readonly open: string; readonly list: boolean; readonly children: readonly Node[] };

const surface = (tok: Tok, src: string): string => src.slice(tok.pos.offset, tok.pos.offset + tok.pos.length);

// Recursively fold the flat token stream into nested nodes. `i` is the
// index of the node's first token; returns the node and the index past it.
const buildNode = (tokens: readonly Tok[], i: number, src: string): { node: Node; next: number } => {
  const tok = tokens[i];
  if (tok === undefined) {
    throw new Error('formatExpr: unexpected end of tokens');
  }
  const open = surface(tok, src);
  const close = CLOSE_FOR[open];
  if (close === undefined) {
    return { node: { kind: 'atom', text: open }, next: i + 1 };
  }
  const collect = (j: number, acc: readonly Node[]): { children: readonly Node[]; next: number } => {
    const t = tokens[j];
    if (t === undefined) {
      throw new Error('formatExpr: unbalanced expression');
    }
    // Closers are their own tokens, so a matching surface char is unambiguous.
    if (surface(t, src) === close) {
      return { children: acc, next: j + 1 };
    }
    const { node, next } = buildNode(tokens, j, src);
    return collect(next, [...acc, node]);
  };
  const { children, next } = collect(i + 1, []);
  const list = tok.t === 'lparen' || tok.t === 'hash-lparen';
  return { node: { kind: 'group', open, list, children }, next };
};

// One-or-more top-level forms (the parser allows several).
const buildForms = (tokens: readonly Tok[], src: string): readonly Node[] => {
  const go = (i: number, acc: readonly Node[]): readonly Node[] => {
    if (i >= tokens.length) {
      return acc;
    }
    const { node, next } = buildNode(tokens, i, src);
    return go(next, [...acc, node]);
  };
  return go(0, []);
};

const closeOf = (open: string): string => CLOSE_FOR[open] ?? ')';

// Collapsed single-line rendering — heads/closers attach without spaces.
const inline = (node: Node): string => {
  if (node.kind === 'atom') {
    return node.text;
  }
  return `${node.open}${node.children.map(inline).join(' ')}${closeOf(node.open)}`;
};

const indent = (n: number): string => ' '.repeat(n);

const isKeyword = (node: Node): boolean => node.kind === 'atom' && node.text.startsWith(':');

// Group nodes into line units: a `:keyword value` stays together as one
// unit so a broken form keeps `:group-by [...]` / `:pairs {...}` on one
// line each. Used for list args and map entries; vectors pass through
// one-per-unit (their `:foo` items are columns, not kwargs).
const chunkPairs = (nodes: readonly Node[]): readonly (readonly Node[])[] => {
  const go = (i: number, acc: readonly (readonly Node[])[]): readonly (readonly Node[])[] => {
    const node = nodes[i];
    if (node === undefined) {
      return acc;
    }
    const next = nodes[i + 1];
    if (isKeyword(node) && next !== undefined) {
      return go(i + 2, [...acc, [node, next]]);
    }
    return go(i + 1, [...acc, [node]]);
  };
  return go(0, []);
};

// Render a line unit at `col`: a lone node, or `:kw value` with the value
// aligned past the keyword so a value that itself breaks lines up cleanly.
const renderUnit = (unit: readonly Node[], col: number): string => {
  const [a, b] = unit;
  if (a === undefined) {
    return '';
  }
  if (b === undefined) {
    return render(a, col);
  }
  // `chunkPairs` only forms a 2-node unit when `a` is a keyword atom, so it
  // always carries flat surface text; fall back defensively if that ever shifts.
  if (a.kind !== 'atom') {
    return unit.map((n) => render(n, col)).join(' ');
  }
  return `${a.text} ${render(b, col + a.text.length + 1)}`;
};

// Block rendering of a group whose inline form exceeds the width budget.
const renderBlock = (node: Extract<Node, { kind: 'group' }>, col: number): string => {
  const close = closeOf(node.open);
  const [head, ...rest] = node.children;
  // Vectors / maps (and empty lists): align children under the first element.
  if (!node.list || head === undefined) {
    if (head === undefined) {
      return `${node.open}${close}`;
    }
    const elemCol = col + node.open.length;
    // Maps pair `:key value`; vectors break per element.
    const units = node.open === '{' ? chunkPairs(node.children) : node.children.map((c) => [c]);
    const lines = units.map((unit, idx) =>
      idx === 0 ? `${node.open}${renderUnit(unit, elemCol)}` : `${indent(elemCol)}${renderUnit(unit, elemCol)}`,
    );
    return `${lines.join('\n')}${close}`;
  }
  // Lists: `(head firstUnit` on the opening line, remaining units aligned
  // under the first argument; each unit keeps `:kw value` together.
  const prefix = `${node.open}${inline(head)}`;
  const argCol = col + prefix.length + 1;
  const [first, ...others] = chunkPairs(rest);
  const firstLine = first === undefined ? prefix : `${prefix} ${renderUnit(first, argCol)}`;
  const lines = [firstLine, ...others.map((unit) => `${indent(argCol)}${renderUnit(unit, argCol)}`)];
  return `${lines.join('\n')}${close}`;
};

const render = (node: Node, col: number): string => {
  if (node.kind === 'atom') {
    return node.text;
  }
  const flat = inline(node);
  if (col + flat.length <= WIDTH) {
    return flat;
  }
  return renderBlock(node, col);
};
